import type { ReactNode } from "react";
import {
  CircleAlert,
  CircleCheck,
  Info,
  Loader2,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@ascleo/ui/components/ui/button";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * Tone of a {@link KioskStatePanel}. One colour family per tone, shared by
 * every flow (KO-11, KO-13, KC-07): `info` (blue), `success` (green),
 * `warning` (amber), `error` (red), `neutral` (grey).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const tone: KioskStateTone = "error";
 * ```
 */
export type KioskStateTone = "info" | "success" | "warning" | "error" | "neutral";

/**
 * An action button inside a state panel. Every action carries an icon (KO-17:
 * retry = `RotateCw`, home = `House`, back = `ArrowLeft`).
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const retry: KioskStateAction = {
 *   label: t("common.retry"),
 *   icon: RotateCw,
 *   onClick: retry,
 *   testId: "scheduler-slots-retry-btn",
 * };
 * ```
 */
export interface KioskStateAction {
  readonly label: string;
  readonly icon: LucideIcon;
  readonly onClick: () => void;
  readonly testId: string;
  readonly disabled?: boolean;
}

interface KioskStateToneStyle {
  readonly panel: string;
  readonly iconTile: string;
  readonly defaultIcon: LucideIcon;
}

const KIOSK_STATE_TONES: Record<KioskStateTone, KioskStateToneStyle> = {
  info: {
    panel: "border-primary-subtle bg-primary-soft",
    iconTile: "bg-primary-subtle text-primary-ink",
    defaultIcon: Info,
  },
  success: {
    panel: "border-success-subtle bg-success-soft",
    iconTile: "bg-success-subtle text-success-ink",
    defaultIcon: CircleCheck,
  },
  warning: {
    panel: "border-warning/40 bg-warning-soft",
    iconTile: "bg-warning/25 text-warning-ink",
    defaultIcon: TriangleAlert,
  },
  error: {
    panel: "border-destructive-subtle bg-destructive-soft",
    iconTile: "bg-destructive-subtle text-destructive-ink",
    defaultIcon: CircleAlert,
  },
  neutral: {
    panel: "border-border bg-foreground-soft",
    iconTile: "bg-foreground-subtle text-foreground",
    defaultIcon: Info,
  },
};

/**
 * Props of {@link KioskStatePanel}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const props: KioskStatePanelProps = { tone: "info", title: "Kein Termin gefunden" };
 * ```
 */
export interface KioskStatePanelProps {
  readonly tone: KioskStateTone;
  /** Translated heading (dark foreground, never tinted). */
  readonly title: ReactNode;
  /** Optional body text (dark foreground, 18px). */
  readonly body?: ReactNode;
  /** Icon element; defaults to the tone's icon. Sized by the panel. */
  readonly icon?: ReactNode;
  /** Filled action, e.g. retry. */
  readonly action?: KioskStateAction;
  /** Outline action next to {@link KioskStatePanelProps.action}, e.g. "Zum Start". */
  readonly secondaryAction?: KioskStateAction;
  /** Additional content between body and actions (facts list, QR code, …). */
  readonly children?: ReactNode;
  readonly testId?: string;
  readonly titleTestId?: string;
  readonly bodyTestId?: string;
}

/**
 * One content-sized panel for empty, error, info, warning and success states
 * (KO-11..17, KC-07, SPK-04). Errors are announced assertively (`role=alert`),
 * other tones politely (`role=status`). The panel never stretches to the step
 * height; centre it with `KioskStep align="center"` when it is the only
 * content. Use {@link KioskLoadingPanel} for pending states.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskStatePanel
 *   tone="error"
 *   title={t("self-service.scheduler.error.title")}
 *   body={t("self-service.scheduler.error.info")}
 *   action={{ label: t("common.retry"), icon: RotateCw, onClick: retry, testId: "slots-retry-btn" }}
 *   secondaryAction={{ label: t("common.home"), icon: House, onClick: home, testId: "slots-home-btn" }}
 * />
 * ```
 */
export function KioskStatePanel({
  tone,
  title,
  body,
  icon,
  action,
  secondaryAction,
  children,
  testId,
  titleTestId,
  bodyTestId,
}: KioskStatePanelProps) {
  const style = KIOSK_STATE_TONES[tone];
  const DefaultIcon = style.defaultIcon;

  return (
    <section
      data-testid={testId}
      data-tone={tone}
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "mx-auto flex w-full max-w-xl min-w-0 flex-col items-center gap-4 rounded-3xl border p-8 text-center",
        style.panel,
      )}
    >
      <KioskStateIconTile className={style.iconTile}>
        {icon ?? <DefaultIcon />}
      </KioskStateIconTile>
      <h2
        data-testid={titleTestId}
        className="m-0 font-heading text-2xl leading-tight font-semibold text-balance text-foreground"
      >
        {title}
      </h2>
      {body ? (
        <p
          data-testid={bodyTestId}
          className="m-0 max-w-[48ch] text-lg leading-normal text-foreground"
        >
          {body}
        </p>
      ) : null}
      {children}
      {action || secondaryAction ? (
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          {secondaryAction ? (
            <KioskStateButton action={secondaryAction} variant="outline" />
          ) : null}
          {action ? <KioskStateButton action={action} variant="default" /> : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Content-sized pending state with a spinner, announced politely with
 * `aria-busy`. Same footprint and typography as {@link KioskStatePanel}.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <KioskLoadingPanel title={t("self-service.scheduler.slots.loading")} />
 * ```
 */
export function KioskLoadingPanel({
  title,
  body,
  testId,
}: {
  readonly title: ReactNode;
  readonly body?: ReactNode;
  readonly testId?: string;
}) {
  return (
    <section
      data-testid={testId}
      role="status"
      aria-busy="true"
      className="mx-auto flex w-full max-w-xl min-w-0 flex-col items-center gap-4 p-8 text-center"
    >
      <Loader2 aria-hidden className="size-16 animate-spin text-primary" />
      <p className="m-0 font-heading text-2xl leading-tight font-semibold text-balance text-foreground">
        {title}
      </p>
      {body ? (
        <p className="m-0 max-w-[48ch] text-lg leading-normal text-foreground">{body}</p>
      ) : null}
    </section>
  );
}

function KioskStateIconTile({
  className,
  children,
}: {
  readonly className: string;
  readonly children: ReactNode;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-16 shrink-0 place-items-center rounded-2xl [&_svg]:size-8",
        className,
      )}
    >
      {children}
    </span>
  );
}

function KioskStateButton({
  action,
  variant,
}: {
  readonly action: KioskStateAction;
  readonly variant: "default" | "outline";
}) {
  const Icon = action.icon;
  return (
    <Button
      type="button"
      data-testid={action.testId}
      variant={variant}
      size="2xl"
      disabled={action.disabled}
      onClick={action.onClick}
    >
      <Icon aria-hidden className="size-6" />
      {action.label}
    </Button>
  );
}
